import {
    COLUMN_LAYOUT_VERSION,
    COLUMN_WIDTH_VERSION,
} from '@/pages/dashboard/dashboard.slice';

const FIXED_DATA_INDEXES = new Set(['random', 'documentNumber', 'fixed-hover-right']);

const isFixed = (dataIndex) => FIXED_DATA_INDEXES.has(dataIndex);

// Stored shape:
// {
//   layoutVersion: number,   // bumped when column composition/keys change
//   widthVersion: number,    // bumped when default widths change
//   order: string[] | null,  // dataIndexes of mid columns; null => default order
//   enabled: { [dataIndex]: boolean },  // user enable/disable overrides
//   widths:  { [dataIndex]: number },   // user width overrides (only resized cols)
// }
//
// Defaults always come from the Redux slice; storage is purely an overlay of
// user-modified fields. Stale versions are dropped silently.

const emptyPrefs = () => ({
    order: null,
    enabled: {},
    widths: {},
    hasStored: false,
});

export const getColumnStorageKey = (currentDashboard, tableId, tableView) => {
    const normalizedView =
        tableView === '.small' || tableView === 'small'
            ? 'small'
            : tableView === '.large' || tableView === 'large'
              ? 'large'
              : '';

    if (!normalizedView) return '';

    const storagePrefix = currentDashboard === 'allSitesDashboard'
        ? 'allSitesDashboard'
        : tableId || 'dashboardTable';

    return `${storagePrefix}.columns.${normalizedView}`;
};

export const loadColumnPrefs = (storageKey) => {
    if (!storageKey) return emptyPrefs();
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return emptyPrefs();

        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            // Legacy array shape or invalid payload: silently drop.
            localStorage.removeItem(storageKey);
            return emptyPrefs();
        }

        const layoutOk = parsed.layoutVersion === COLUMN_LAYOUT_VERSION;
        const widthOk = parsed.widthVersion === COLUMN_WIDTH_VERSION;

        return {
            order: layoutOk && Array.isArray(parsed.order) ? parsed.order : null,
            enabled:
                layoutOk && parsed.enabled && typeof parsed.enabled === 'object'
                    ? { ...parsed.enabled }
                    : {},
            widths:
                widthOk && parsed.widths && typeof parsed.widths === 'object'
                    ? { ...parsed.widths }
                    : {},
            hasStored: true,
        };
    } catch {
        localStorage.removeItem(storageKey);
        return emptyPrefs();
    }
};

export const saveColumnPrefs = (storageKey, prefs) => {
    if (!storageKey) return;
    const payload = {
        layoutVersion: COLUMN_LAYOUT_VERSION,
        widthVersion: COLUMN_WIDTH_VERSION,
        order: Array.isArray(prefs?.order) && prefs.order.length > 0 ? prefs.order : null,
        enabled: prefs?.enabled && typeof prefs.enabled === 'object' ? prefs.enabled : {},
        widths: prefs?.widths && typeof prefs.widths === 'object' ? prefs.widths : {},
    };
    localStorage.setItem(storageKey, JSON.stringify(payload));
};

export const updateColumnPrefs = (storageKey, updater) => {
    const current = loadColumnPrefs(storageKey);
    const next = updater(current) || current;
    saveColumnPrefs(storageKey, next);
    return next;
};

export const applyPrefsToColumns = (columns, prefs) => {
    if (!columns || columns.length === 0) return columns;

    const checkboxCol = columns.find((c) => c.dataIndex === 'random');
    const docCol = columns.find((c) => c.dataIndex === 'documentNumber');
    const fixedRight = columns.find((c) => c.dataIndex === 'fixed-hover-right');
    const mids = columns.filter((c) => !isFixed(c.dataIndex));
    const midByIdx = new Map(mids.map((c) => [c.dataIndex, c]));

    let ordered;
    if (Array.isArray(prefs?.order) && prefs.order.length > 0) {
        const seen = new Set();
        ordered = [];
        prefs.order.forEach((di) => {
            if (isFixed(di)) return;
            const col = midByIdx.get(di);
            if (col && !seen.has(di)) {
                ordered.push(col);
                seen.add(di);
            }
        });
        // Append columns introduced after the saved order (e.g. new feature column).
        mids.forEach((c) => {
            if (!seen.has(c.dataIndex)) ordered.push(c);
        });
    } else {
        ordered = mids;
    }

    const enabledOverrides = prefs?.enabled || {};
    const widthOverrides = prefs?.widths || {};

    const merged = ordered.map((c) => {
        const out = { ...c };
        if (Object.prototype.hasOwnProperty.call(enabledOverrides, c.dataIndex)) {
            out.isEnabled = !!enabledOverrides[c.dataIndex];
        }
        if (Object.prototype.hasOwnProperty.call(widthOverrides, c.dataIndex)) {
            out.width = widthOverrides[c.dataIndex];
        }
        return out;
    });

    const result = [];
    if (checkboxCol) result.push({ ...checkboxCol, isEnabled: true });
    if (docCol) result.push({ ...docCol, isEnabled: true });
    result.push(...merged);
    if (fixedRight) result.push(fixedRight);
    return result;
};

export const reconcileColumnsWithStorage = (columns, setColumns, storageKey) => {
    if (!storageKey || !columns || columns.length === 0) return;

    const prefs = loadColumnPrefs(storageKey);
    if (prefs.hasStored) {
        // Re-save to migrate to current versions silently. No write if nothing
        // was stored to begin with, so first-time users don't get noise in LS.
        saveColumnPrefs(storageKey, prefs);
    }

    setColumns(applyPrefsToColumns(columns, prefs));
};

// Accepts an array of column keys (strings) OR column-like objects with key/dataIndex.
export const setEnabledColumns = (selected, columns, setColumns, storageKey) => {
    if (!storageKey || !selected || !columns || columns.length === 0) return;

    const matchesSelection = (col) =>
        selected.some((item) => {
            if (item == null) return false;
            if (typeof item === 'string') return item === col.key;
            return item.dataIndex === col.dataIndex || item.key === col.key;
        });

    const enabledMap = {};
    columns.forEach((col) => {
        if (isFixed(col.dataIndex)) return;
        enabledMap[col.dataIndex] = matchesSelection(col);
    });

    const next = updateColumnPrefs(storageKey, (prev) => ({
        ...prev,
        enabled: enabledMap,
    }));
    setColumns(applyPrefsToColumns(columns, next));
};

export const onReorder = (orderedItems, columns, setColumns, storageKey) => {
    if (!storageKey || !orderedItems || !columns || columns.length === 0) return;

    const order = orderedItems
        .map((it) => it?.dataIndex)
        .filter((di) => di && !isFixed(di));

    const next = updateColumnPrefs(storageKey, (prev) => ({ ...prev, order }));
    setColumns(applyPrefsToColumns(columns, next));
};

// Persist a single user-driven width change. Fixed columns are ignored.
export const setColumnWidth = (storageKey, dataIndex, width) => {
    if (!storageKey || !dataIndex || isFixed(dataIndex)) return null;
    if (typeof width !== 'number' || !isFinite(width) || width <= 0) return null;
    return updateColumnPrefs(storageKey, (prev) => ({
        ...prev,
        widths: { ...prev.widths, [dataIndex]: width },
    }));
};

// Persist an order derived from a list of column-like objects (used by drag-drop).
export const setColumnOrderFromColumns = (storageKey, orderedColumns) => {
    if (!storageKey || !orderedColumns) return null;
    const order = orderedColumns
        .map((c) => c?.dataIndex)
        .filter((di) => di && !isFixed(di));
    return updateColumnPrefs(storageKey, (prev) => ({ ...prev, order }));
};

// Ant's drag indexes include the selection and fixed document columns.
// Keep hidden columns in Redux so they remain available in the column picker.
export const reorderVisibleDashboardColumns = (columns, fromIndex, toIndex) => {
    const visible = columns.filter((column) => !isFixed(column.dataIndex) && column.isEnabled !== false);
    const from = fromIndex - 2;
    if (from < 0 || from >= visible.length) return null;

    const [moved] = visible.splice(from, 1);
    const to = Math.max(0, Math.min(toIndex - 2, visible.length));
    visible.splice(to, 0, moved);

    const hidden = columns.filter((column) => !isFixed(column.dataIndex) && column.isEnabled === false);
    const document = columns.find((column) => column.dataIndex === 'documentNumber');
    const formats = columns.find((column) => column.dataIndex === 'fixed-hover-right');

    return [document, ...visible, ...hidden, formats].filter(Boolean);
};

export const getDashboardEmptyStateKind = ({
    currentDashboard,
    isFavouriteTab,
    favouriteCount,
    hasActiveSearch,
    allItemsCount,
}) => {
    if (isFavouriteTab && !hasActiveSearch && favouriteCount === 0) return 'no-favourites';
    if (isFavouriteTab && hasActiveSearch) return 'filtered';
    if (allItemsCount > 0) return 'filtered';
    if (currentDashboard === 'documentDashboard') return 'no-documents';
    return null;
};
